import "./SignupComponent.scss";
import {Field, Form, Formik, FormikHelpers} from "formik";
import {ReactNode, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import FormInput from "@/components/form-components/form-input.tsx";
import {Button} from "@/components/ui/button.tsx";
import {motion} from "framer-motion";
import {LOGIN_ROUTE, OTP_ROUTE} from "@/constants/RoutesConfig.tsx";
import FormPasswordInput from "@/components/form-components/formik-password-input.tsx";
import {LockIcon, Mail, UserRound} from "lucide-react";
import GoogleLoginComponent from "@/screens/auth/google-login/GoogleLoginComponent.tsx";
import FormikCheckbox from "@/components/form-components/formik-checkbox.tsx";
import OrSeparatorComponent from "@/components/common-components/or-separator/OrSeparatorComponent.tsx";
import {CommonService} from "@/services";
import commonService from "@/services/common.service.ts";
import {SignUpValidationSchema} from "@/services/validations/auth.validation.ts";
import {authFormVariants, containerVariants, getFadeInUpVariant, inputVariants} from "@/constants/animations.ts";
import {ENV} from "@/constants";
import { GoogleOAuthProvider } from "@react-oauth/google";
const clientId = ENV.GOOGLE_CLIENT_ID;

interface SignupValuesType {
    name: string;
    email: string;
    password: string;
    termsAccepted: boolean;
}

const initialValues: SignupValuesType = {
    name: "",
    email: "",
    password: "",
    termsAccepted: false
};

const SignupComponent = () => {
    const navigate = useNavigate();

    const onCreateUser = useCallback(
        (
            payload: SignupValuesType,
            {setSubmitting, setErrors}: FormikHelpers<SignupValuesType>
        ) => {
            setSubmitting(true);
            
            CommonService._account
                .create_user(payload)
                .then(() => {
                    // Track successful signup
                    CommonService.handleMixpanelTrack("Signup Success", {
                        email: payload.email,
                        name: payload.name,
                        timestamp: new Date().toISOString()
                    });
                    
                    CommonService._alert.showToast("Otp sent successfully", "success");
                    navigate(`${OTP_ROUTE}?email=${payload.email}`);
                })
                .catch((error: any) => {
                    // Track signup failure
                    CommonService.handleMixpanelTrack("Signup Failed", {
                        email: payload.email,
                        name: payload.name,
                        error: error?.detail?.error || "Unknown error",
                        timestamp: new Date().toISOString()
                    });
                    
                    commonService.handleErrors(setErrors, error, true);
                })
                .finally(() => setSubmitting(false));
        },
        [navigate]
    );


    return (
        <GoogleOAuthProvider clientId={clientId}>

        <motion.div
            className="signup-component"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
        >
            <motion.div
                className="auth-screen-title-wrapper"
                variants={getFadeInUpVariant(0.4)}
                initial="initial"
                animate="animate"
            >
                <div className="auth-screen-title">Create your account</div>
                <motion.div
                    className="auth-screen-subtitle text-secondary-foreground"
                    variants={containerVariants}
                    initial="initial"
                    animate="animate"
                >
                    Already have an account?
                    <Button
                        type={"button"}
                        variant={"link"}
                        fullWidth={false}
                        className={"ml-[-11px] text-md"}
                        onClick={() => navigate(LOGIN_ROUTE)}
                    >
                        Sign in
                    </Button>
                </motion.div>
            </motion.div>
            <GoogleLoginComponent/>
            <OrSeparatorComponent/>
            <Formik
                initialValues={initialValues}
                validationSchema={SignUpValidationSchema}
                validateOnChange={true}
                validateOnBlur={false}
                enableReinitialize={true}
                validateOnMount={false}
                onSubmit={onCreateUser}
            >
                {({isSubmitting}) => (
                    <Form noValidate={true}>
                        <motion.div
                            className="form-controls"
                            variants={authFormVariants}
                            initial="initial"
                            animate="animate"
                        >
                            <div className="auth-form-content">
                                <Field name={"name"}>
                                    {(field: any) => (
                                        <FormInput
                                            label={"Full Name"}
                                            className={"full-width"}
                                            placeholder={""}
                                            formikField={field}
                                            icon={<UserRound
                                                className="size-4 text-gray-500 peer-focus:text-blue-500"/>}
                                            type={"email"}
                                            size={"medium"}
                                        />
                                    )}
                                </Field>
                                <Field name={"email"}>
                                    {(field: any) => (
                                        <FormInput
                                            label={"Email"}
                                            className={"full-width"}
                                            placeholder={""}
                                            formikField={field}
                                            icon={<Mail
                                                className="size-4 text-gray-500 peer-focus:text-blue-500"/>}
                                            type={"email"}
                                            size={"medium"}
                                        />
                                    )}
                                </Field>
                                <Field name="password">
                                    {(field: any) => (
                                        <FormPasswordInput
                                            name="password"
                                            label="Password"
                                            type="password"
                                            placeholder=""
                                            icon={<LockIcon
                                                className="size-4 text-gray-500 peer-focus:text-blue-500"/>}
                                            formikField={field}
                                        />
                                    )}
                                </Field>
                                <motion.div
                                   variants={inputVariants}
                                   initial="initial"
                                   animate="animate"
                                >
                                    <Field name="termsAccepted">
                                        {(field: any) => (
                                            <FormikCheckbox
                                                formikField={field}
                                                label={
                                                    <span>
                                                        Agree to our{' '}
                                                        <a 
                                                            href="https://www.superengineer.ai/terms-and-conditions" 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-foreground underline font-medium hover:text-primary transition-colors"
                                                            aria-label="Terms of Service - Opens in new tab"
                                                            onClick={(e) =>{ e.stopPropagation(); 
                                                                CommonService.handleMixpanelTrack("Terms of Service Clicked", {
                                                                timestamp: new Date().toISOString()
                                                            });}}
                                                        >
                                                            Terms of Service
                                                        </a>
                                                        {' '}and{' '}
                                                        <a 
                                                            href="https://www.superengineer.ai/privacy-policy" 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-foreground underline font-medium hover:text-primary transition-colors"
                                                            aria-label="Privacy Policy - Opens in new tab"
                                                            onClick={(e) => { e.stopPropagation(); 
                                                                CommonService.handleMixpanelTrack("Privacy Policy Clicked", {
                                                                    timestamp: new Date().toISOString()
                                                                });
                                                            }}
                                                        >
                                                            Privacy Policy
                                                        </a>
                                                    </span>
                                                }
                                                {...field}
                                            />
                                        )}
                                    </Field>
                                </motion.div>
                                <motion.div
                                    variants={getFadeInUpVariant(0.4)}
                                    initial="initial"
                                    animate="animate"
                                >
                                    <Button
                                        type={"submit"}
                                        className={"full-width mrg-top-30"}
                                        size={"lg"}
                                        fullWidth={true}
                                        loading={isSubmitting}
                                    >
                                        Sign up
                                    </Button>
                                </motion.div>
                            </div>
                        </motion.div>
                    </Form>
                ) as ReactNode
                }
            </Formik>
        </motion.div>
        </GoogleOAuthProvider> as ReactNode

    );
};

export default SignupComponent;
